import axios from "axios";
import { Field, Form, Formik } from "formik";
import { fieldToTextField } from "formik-mui";
import { get, startCase, toLower, trim } from "lodash";
import PropTypes from "prop-types";
import React from "react";
import VisitorAPI from "visitorapi";
import * as Yup from "yup";

// @material-ui/core components
import { FormHelperText } from "@mui/material";
import MuiCheckbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import { withStyles } from "@mui/styles";

// @material-ui/icons
import Check from "@mui/icons-material/Check";

// core components
import {
  Button,
  Card,
  CardBody,
  GridContainer,
  GridItem,
} from "~/shared/components/MuiComponents.jsx";

import TestimonialsCarousel from "~/shared/widgets/TestimonialsCarousel.jsx";
import TrustBox from "~/shared/widgets/TrustBox.jsx";

import consultationStyles from "../styles/consultationStyles.js";

import AutocompleteUSStates, {
  stateSuggestions,
} from "./autocompleteUSStates.jsx";

import { FormikTextField, Headline, MAX_WIDTH } from "./shared.jsx";
import { FulfillmentOptions, SignupSteps } from "./signupSteps.jsx";

import { identifyUser } from "~/shared/utils/analytics";
import { bugsnagClient } from "~/shared/utils/errorHandling";
import {
  getZerobounceAPIKey,
  isPickupAvailableForConsultationType,
  supportedUSStates,
} from "~/shared/utils/sharedUtils";

import {
  COLOR_KICK_BLUE,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA,
  CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY,
  CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP,
} from "~/shared/sharedConstants.js";

import { trackSignupEvent } from "~/shared/utils/signupTracing";

import { VISITOR_API_KEY } from "~/visits/utils/visitorState";
import { INITIAL_CONSULTATION_TYPE_FOR_FOLLOW_UP } from "~/visits/utils/visitUtils";

import { kickBlue, kickLightBlue } from "~/shared/styles/baseStyles.js";

import {
  areCookiesAvailable,
  clearUserAndVisitCookies,
  getUserID,
  getVisitEmail,
  getVisitGender,
  getVisitPhone,
  getVisitUSState,
  clearVisitCookies,
  isLocalStorageAvailable,
  logVisitUserOut,
  setFunnelUserCookiesFromPatient,
  setUserAlreadyExists,
  setVisitUserCookies,
} from "../utils/visitCookies";
import { fetchLoggedInPatient } from "../utils/loggedInPatient";

import { isLoggedIn } from "~/patient-portal/utils/portalCookies";

const initialValues = {
  usState: { label: "", value: "" },
  firstName: "",
  lastName: "",
  email: "",
  // Unchecked by default. A pre-ticked consent box is close to worthless as evidence
  // that the customer agreed to anything -- they never took an action -- and
  // affirmative consent is what ROSCA and the state automatic-renewal laws ask for.
  // Validation (Yup `oneOf([true])`) already blocks submission until it is ticked.
  terms: false,
};

// const TextFieldUSStates = (props) => {
class TextFieldUSStates extends React.Component {
  constructor(props) {
    super(props);
  }

  render() {
    return (
      <div
        style={{
          marginTop: "10px",
        }}
      >
        <AutocompleteUSStates
          {...fieldToTextField(this.props)}
          value={this.props.value}
          formikProps={this.props.formikProps}
          initialValue={this.props.initialValue}
        />
      </div>
    );
  }
}

class SignUp extends React.Component {
  emailAbstractValidated = false;

  constructor(props) {
    super(props);
    this.state = {
      consultationType: this.props.match.params.consultationType,
      checked: [],
      showComingSoon: false,
      signupError: "",
      visitorAPIRegion: "",
      initialStateSuggestion: null,
      disableSubmit: false,
      // A patient who is already logged in: "checking" while their account
      // loads, then the patient record to offer "Continue as …". Null shows
      // the signup form.
      loggedInPatient: null,
      checkingLoggedInPatient: false,
    };

    this.formRef = React.createRef();

    var item = sessionStorage.getItem("visApiData");

    var visApiData = item ? JSON.parse(item) : null;

    if (visApiData && visApiData.region) {
      var suggestion = stateSuggestions.find(
        (v) => v.value == visApiData.region.toUpperCase(),
      );

      this.state.visitorApiRegion = visApiData.region;
      this.state.initialStateSuggestion = suggestion;
    } else {
      VisitorAPI(VISITOR_API_KEY)
        .then((data) => {
          // console.log("### visitor API data:", data);

          sessionStorage.setItem("visApiData", JSON.stringify(data));
          visApiData = data;

          var suggestion = stateSuggestions.find(
            (v) => v.value == visApiData.region.toUpperCase(),
          );

          this.setState({
            visitorAPIRegion: visApiData.region,
            initialStateSuggestion: suggestion,
          });

          trackSignupEvent("signup.visitor_api_resolved", {
            region: visApiData.region,
            suggestion_found: !!suggestion,
          });

          this.formRef.current?.props.formikProps.setFieldValue(
            "usState",
            suggestion,
          );
        })
        .catch((error) => {
          console.error("Error calling visitorAPI: ", error);
        });
    }
  }

  disableSubmitAndGoBack() {
    this.setState({ disableButton: true });
    this.props.history.goBack();
  }

  componentDidMount() {
    // alert on ios - only support safari because of react-webcam not supporting WKV on iOS
    if (!isLocalStorageAvailable() || !areCookiesAvailable()) {
      alert(
        "Unfortunately your current browser isn't supported - Please disable incognito mode or try another browser or device.",
      );
      this.disableSubmitAndGoBack();
      return;
    }

    if (
      isLoggedIn() &&
      getUserID() &&
      getVisitEmail() &&
      getVisitUSState() &&
      getVisitGender() &&
      getVisitPhone()
    ) {
      const consultationType = this.props.match.params.consultationType;
      this.props.history.push(`/visits/${consultationType}/1`);
      return;
    }

    trackSignupEvent("signup.page_load", {
      consultation_type: this.state.consultationType,
      has_cached_visitor_api: !!sessionStorage.getItem("visApiData"),
      user_agent: navigator.userAgent,
    });

    // A logged-in patient without a complete set of funnel cookies (they came
    // from an email link or the homepage, or the portal's login never set them)
    // used to have their user cookies cleared here while their login token
    // stayed. The portal still showed them logged in, but the funnel didn't
    // know them, so Start Visit sent them back here and the form's "account
    // exists" 409 sent them back to the portal, in a loop. Offer to continue
    // as them instead; anything else starts a fresh signup.
    if (isLoggedIn()) {
      this.offerContinueAsLoggedInPatient();
      return;
    }
    this.startFreshSignup();
  }

  // startFreshSignup clears what a previous visit left behind so a new
  // visitor's signup starts clean (the long-standing behavior here).
  startFreshSignup() {
    clearUserAndVisitCookies();
    localStorage.clear();
  }

  async offerContinueAsLoggedInPatient() {
    this.setState({ checkingLoggedInPatient: true });
    let patient = null;
    try {
      patient = await fetchLoggedInPatient();
    } catch (error) {
      console.error("[signup] could not load the logged-in patient: ", error);
    }
    if (patient) {
      this.setState({
        loggedInPatient: patient,
        checkingLoggedInPatient: false,
      });
      return;
    }
    // The token didn't resolve to a patient: log out fully rather than keep a
    // login the funnel can't use, so the form's 409 path leads to a real login.
    logVisitUserOut();
    this.startFreshSignup();
    this.setState({ checkingLoggedInPatient: false });
  }

  continueAsLoggedInPatient = () => {
    const consultationType = this.props.match.params.consultationType;
    clearVisitCookies();
    setFunnelUserCookiesFromPatient(this.state.loggedInPatient);
    trackSignupEvent("signup.continue_as_logged_in", {
      consultation_type: consultationType,
    });
    this.props.history.push(`/visits/${consultationType}/1`);
  };

  signOutLoggedInPatient = () => {
    logVisitUserOut();
    this.startFreshSignup();
    trackSignupEvent("signup.continue_as_declined", {
      consultation_type: this.state.consultationType,
    });
    this.setState({ loggedInPatient: null });
  };

  renderContinueAsLoggedInPatient() {
    const { classes } = this.props;
    const firstName = startCase(
      toLower(trim(this.state.loggedInPatient.firstName || "")),
    );
    return (
      <div
        style={{
          maxWidth: `${MAX_WIDTH}px`,
          margin: "16px auto 24px",
          display: "flex",
          flexDirection: "column",
          gap: "14px",
        }}
      >
        <p style={{ margin: 0, fontSize: "16px" }}>
          You're logged in{firstName ? ` as ${firstName}` : ""}. Continue to
          start your visit.
        </p>
        <Button
          className={classes.signupButton}
          onClick={this.continueAsLoggedInPatient}
        >
          {firstName ? `Continue as ${firstName}` : "Continue"}
        </Button>
        <p style={{ margin: 0, textAlign: "center" }}>
          Not you?{" "}
          <button
            type="button"
            onClick={this.signOutLoggedInPatient}
            style={{
              background: "none",
              border: 0,
              padding: 0,
              font: "inherit",
              color: kickBlue,
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            Log out
          </button>
        </p>
      </div>
    );
  }

  handleToggle = (value) => {
    const { checked } = this.state;
    const currentIndex = checked.indexOf(value);
    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    this.setState({
      checked: newChecked,
    });
  };

  validationSchema = Yup.object().shape({
    firstName: Yup.string()
      .required("First Name is required.")
      .max(35, "First Name is too long"),
    lastName: Yup.string()
      .required("Last Name is required.")
      .max(35, "Last Name is too long"),
    email: Yup.string()
      .email("Please enter a valid email address")
      .required("Email is required.")
      .max(254, "Email is too long"),
    usState: Yup.object().shape({
      value: Yup.string().required(),
    }),
    terms: Yup.boolean().oneOf(
      [true],
      "Please agree to terms and privacy policy to continue",
    ),
  });

  // TODO: refactor this to use react-hook-form and hooks
  TermsCheckbox = (props) => {
    const classes = this.props.classes;
    // Drives the error styling as well as the message. Previously only the
    // helper text below reacted, so the copy said "click the checkbox" while
    // the box itself kept its resting border and looked perfectly fine.
    const hasError = props.form.errors[props.field.name] != null;
    const iconClass = (base) =>
      hasError ? `${base} ${classes.consentIconError}` : base;

    const toggle = () =>
      props.field.value == true
        ? props.form.setFieldValue(props.field.name, null)
        : props.form.setFieldValue(props.field.name, true);

    return (
      <React.Fragment>
        <div
          className={
            hasError
              ? `${classes.consentRow} ${classes.consentRowError}`
              : classes.consentRow
          }
          // The whole panel toggles. The label already covers its own row
          // natively, so this only fires for clicks that land on the panel
          // ITSELF -- the padding, and the helper line, which passes clicks
          // through via pointer-events: none. Guarding on target === currentTarget
          // is what stops a click on the box or label toggling twice and
          // cancelling itself out.
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              toggle();
            }
          }}
        >
          <FormControlLabel
            // margin 0 undoes MUI's default -11px left pull, which otherwise
            // hangs the box outside the panel padding. Alignment stays centred:
            // the checkbox root carries 14px of its own padding, so flex-start
            // aligns that PADDING with the text and drops the visible box a line
            // below it.
            style={{ margin: 0, width: "100%" }}
            control={
              <MuiCheckbox
                checked={Boolean(props.field.value)}
                onClick={toggle}
                checkedIcon={
                  <Check className={iconClass(classes.consentCheckedIcon)} />
                }
                icon={
                  <Check className={iconClass(classes.consentUncheckedIcon)} />
                }
                classes={{
                  checked: classes.checked,
                  root: classes.consentCheckRoot,
                }}
              />
            }
            label={
              <span
                className={hasError ? classes.consentLabelError : undefined}
                style={{
                  fontSize: "13px",
                  color: hasError ? undefined : kickBlue,
                }}
              >
                I agree to{" "}
                <a
                  href="/terms"
                  className={classes.consentLabelLink}
                  target="_blank"
                  // A link inside a <label> should not activate the control.
                  // Browsers mostly honour that for interactive descendants,
                  // but it is one keystroke to guarantee rather than rely on.
                  onClick={(event) => event.stopPropagation()}
                  style={{ textDecoration: "underline" }}
                >
                  Terms of Use
                </a>{" "}
                <span>&</span>{" "}
                <a
                  href="/privacy"
                  className={classes.consentLabelLink}
                  target="_blank"
                  onClick={(event) => event.stopPropagation()}
                  style={{ textDecoration: "underline" }}
                >
                  Privacy Policy
                </a>
              </span>
            }
          />
          {hasError ? (
            <FormHelperText error className={classes.consentHelper}>
              Please agree to continue
            </FormHelperText>
          ) : null}
        </div>
      </React.Fragment>
    );
  };
  validateEmail = async (email) => {
    let result = await this.validateEmailAbstractAPI(email);

    if (!result.emailValid) {
      result = await this.validateEmailZeroBounce(email);
      return result;
    }

    return result;
  };

  validateEmailAbstractAPI = async (email) => {
    const url = `https://emailvalidation.abstractapi.com/v1/?api_key=81c6f09d19cc4679b201a3ab23edba17&email=${email}`;

    try {
      const response = await axios.get(url);

      const data = response.data;

      if (data.deliverability == "DELIVERABLE") {
        this.emailAbstractValidated = true;
        return { emailValid: true };
      } else {
        // Email is not valid
        let suggestion = data.autocorrect
          ? `Did you mean: ${data.autocorrect}?`
          : null;

        return { emailValid: false, suggestion };
      }
    } catch (error) {
      console.error("Exception validating email:", error);
      return { emailValid: false };
    }
  };

  validateEmailZeroBounce = async (email) => {
    const url = `https://api.zerobounce.net/v2/validate?api_key=${getZerobounceAPIKey()}&email=${encodeURIComponent(
      email.trim(),
    )}`;
    try {
      const response = await axios.get(url);

      const data = response.data;

      if (data.status === "valid") {
        this.emailAbstractValidated = true;
        return { emailValid: true };
      } else {
        let suggestion = data.did_you_mean
          ? `Did you mean: ${data.did_you_mean}?`
          : null;
        return { emailValid: false, suggestion };
      }
    } catch (error) {
      console.error("Exception validating email:", error);
      return { emailValid: false };
    }
  };

  emailOnChange = (e) => {
    this.emailAbstractValidated = false;

    let trimmedEmail = trim(e.target.value);
    if (trimmedEmail == "") {
      return;
    }

    this.validateEmail(trimmedEmail);
  };

  renderSignupForm() {
    const { classes } = this.props;

    return (
      <React.Fragment>
        {/* This takes most people about 10 minutes */}

        <CardBody
          style={{
            padding: "0",
            marginTop: "12px",
            maxWidth: MAX_WIDTH,
            justifyContent: "center",
            display: "flex",
            flexDirection: "column",
            margin: "auto",
          }}
        >
          <Formik
            initialValues={{
              ...initialValues,
              // try to select the initial state based on visitor API geolocation
              usState:
                this.state.initialStateSuggestion || initialValues.usState,
            }}
            validationSchema={this.validationSchema}
            onSubmit={async (values, { setSubmitting, setErrors }) => {
              // email hasn't been validated yet... just return
              // if (!this.emailAbstractValidated) {
              //   return;
              // }

              let { emailValid, suggestion } = await this.validateEmail(
                values.email,
              );

              if (!emailValid) {
                if (suggestion) {
                  setErrors({
                    email: `Could not validate email. ${suggestion}`,
                  });
                  return;
                } else {
                  setErrors({
                    email:
                      "Could not validate email. Please check and try again.",
                  });

                  return;
                }
              }

              const { firstName, lastName, email } = values;

              var signupValues = {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                email: email.trim(),
                usState: values.usState.value,
                consultationType: this.state.consultationType,
              };

              // Attach Rewardful details to signup values if they exist
              if (
                typeof Rewardful !== "undefined" &&
                Rewardful &&
                Rewardful.referral
              ) {
                signupValues = {
                  ...signupValues,
                  signupReferralID: Rewardful.referral,
                  signupAffiliateID: get(Rewardful, "affiliate.id"),
                  signupCampaignID: get(Rewardful, "campaign.id"),
                  signupCouponID: get(Rewardful, "coupon.id"),
                };
              }

              // console.log("$$$ signupValues: ", signupValues);

              trackSignupEvent("signup.form_submit", {
                consultation_type: this.state.consultationType,
                us_state: values.usState.value,
                visitor_api_region: this.state.visitorAPIRegion,
              });

              setSubmitting(true);
              axios
                .post("/api/v1/signup", signupValues)
                .then((response) => {
                  console.log("Response received:", response);
                  //
                  //
                  // not yet in the user's state - show coming soon
                  if (!supportedUSStates.includes(values.usState.value)) {
                    this.setState({ showComingSoon: true });
                    return;
                  }

                  const jsonData = response.data.data;
                  // console.log("jsonDATA:", jsonData);

                  // Block self referrals
                  if (
                    jsonData.attributes.affiliateID ===
                    window.Rewardful?.affiliate.id
                  ) {
                    delete window.Rewardful?.coupon;
                  }

                  setVisitUserCookies(jsonData);

                  // rewardful client-side conversion tracking. JANKY: only calling this in production because Rewardful doens't support stripe TEST environment yet, causing rewardful call to fail and cookie to get deleted
                  if (process.env.NODE_ENV == "master") {
                    if (rewardful && Rewardful.referral) {
                      rewardful("convert", {
                        email: toLower(email).trim(),
                      });
                    }
                  }

                  bugsnagClient.user = {
                    email: toLower(email).trim(),
                    name: startCase(`${firstName} ${lastName}`),
                  };

                  identifyUser(jsonData.id, {
                    email: toLower(email).trim(),
                  });

                  setSubmitting(false);

                  // T-139 submit form here for checking username, password etc
                  //
                  if (jsonData.attributes.apiTokenJWT) {
                    trackSignupEvent("signup.navigation_to_details", {
                      consultation_type: this.state.consultationType,
                      us_state: values.usState.value,
                    });

                    // A successful signup means a brand-new account (an
                    // existing email 409s to /me/ in the catch below) — it
                    // cannot have the prior initial visit a follow-up
                    // requires, so continue in the matching INITIAL funnel.
                    const initialConsultationType =
                      INITIAL_CONSULTATION_TYPE_FOR_FOLLOW_UP[
                        this.state.consultationType
                      ];
                    const location = {
                      pathname: `/visits/${
                        initialConsultationType || this.state.consultationType
                      }/signup-details`,
                      // state: { signUp: values }
                    };
                    this.props.history.push(location);
                  } else {
                    console.error("Error: Invalid Login Token received");
                  }
                })
                .catch((error) => {
                  setSubmitting(false);
                  if (error.response) {
                    if (error.response.status === 409) {
                      setUserAlreadyExists(
                        error.response.data?.data?.attributes?.email,
                      );
                      window.location.href = "/me/";
                      return;
                    }
                    this.setState({
                      signupError: get(
                        error.response,
                        "data.errors[0].title",
                        "",
                      ),
                    });
                  }
                  console.error("Error signing up: ", error);
                  bugsnagClient.notify(error);
                })
                .finally(() => {
                  this.setState({ disableSubmit: false });
                });
            }}
          >
            {(props) => {
              // Disable autofill on Android WebView (Facebook in-app browser) to prevent
              // autofill bridge crash (setContactAutofillValuesFromBridge)
              const isAndroidWebView = /Android.*wv|FB_IAB|FBAN|FBAV/.test(
                navigator.userAgent,
              );
              const autoCompleteValue = isAndroidWebView ? "off" : undefined;

              return (
                <Form
                  data-rewardful="true"
                  autoComplete={autoCompleteValue}
                  style={{
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                  onChange={() => {
                    this.setState({
                      disableSubmit: false,
                    });
                  }}
                >
                  <Field
                    required
                    id="email"
                    name="email"
                    labeltext="Email Address"
                    placeholder="e.g. johndoe@gmail.com"
                    component={FormikTextField}
                    variant="outlined"
                    fullWidth
                    type="email"
                    inputProps={{ autoComplete: autoCompleteValue }}
                  />

                  <Field
                    id="firstName"
                    name="firstName"
                    labeltext="First Name"
                    placeholder="Your First Name"
                    component={FormikTextField}
                    variant="outlined"
                    fullWidth
                    inputProps={{ autoComplete: autoCompleteValue }}
                  />

                  <Field
                    id="lastName"
                    name="lastName"
                    labeltext="Last Name"
                    placeholder="Your Last Name"
                    component={FormikTextField}
                    variant="outlined"
                    fullWidth
                    inputProps={{ autoComplete: autoCompleteValue }}
                  />

                  <Field
                    id="usState"
                    name="usState"
                    formikProps={props}
                    innerRef={this.formRef}
                    initialValue={this.state.initialStateSuggestion}
                    component={TextFieldUSStates}
                    variant="outlined"
                    fullWidth
                  />
                  <div className={classes.center} style={{ marginTop: "20px" }}>
                    <Field
                      id="terms"
                      name="terms"
                      component={this.TermsCheckbox}
                      type="checkbox"
                    />

                    <div style={{ color: "red", margin: "6px auto 6px auto" }}>
                      <p style={{ textAlign: "center" }}>
                        {this.state.signupError}
                      </p>
                    </div>
                    <Button
                      disabled={!!this.state.disableSubmit}
                      onClick={() => {
                        this.setState({ disableSubmit: true }, () => {
                          props.submitForm().then(() => {
                            // Re-enable button if validation failed (onSubmit was never called)
                            if (!props.isSubmitting) {
                              this.setState({ disableSubmit: false });
                              // Track validation failures — if we got here without submitting,
                              // Formik validation blocked the submit
                              const errors = props.errors;
                              if (errors && Object.keys(errors).length > 0) {
                                trackSignupEvent(
                                  "signup.form_validation_error",
                                  {
                                    missing_fields:
                                      Object.keys(errors).join(","),
                                    us_state_value: JSON.stringify(
                                      props.values.usState,
                                    ),
                                    visitor_api_region:
                                      this.state.visitorAPIRegion,
                                    has_initial_state_suggestion:
                                      !!this.state.initialStateSuggestion,
                                  },
                                );
                              }
                            }
                          });
                        });
                      }}
                      className={classes.signupButton}
                    >
                      {this.renderButtonLabel()}
                      {props.isSubmitting ? (
                        <CircularProgress
                          size="large"
                          style={{
                            position: "relative",
                            left: "24px",
                            top: "1px",
                            color: kickBlue,
                          }}
                        />
                      ) : null}
                    </Button>
                    <div style={{ marginTop: "4px" }}>
                      <a href="/me/">Login to your account</a>
                    </div>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </CardBody>
      </React.Fragment>
    );
  }

  renderBenefitHeadline() {
    var headline = "Let's Start Your Visit";
    switch (this.state.consultationType) {
      case CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY:
      case CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP:
        headline = "Let's Start Your Visit to Confidently Cool";
        break;

      case CONSULTATION_TYPE_ABBREVIATION_INSOMNIA:
      case CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP:
        headline =
          "Start Your Visit From 'Sleep Deprived' to 'Energized and Alive'";
        break;
    }
    return headline;
  }

  renderSubhead() {
    return "Your easy prescription intake only takes 10 minutes";
  }

  renderButtonLabel() {
    switch (this.state.consultationType) {
      case CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY:
      case CONSULTATION_TYPE_ABBREVIATION_PERFORMANCE_ANXIETY_FOLLOW_UP:
        return "Start Confidence Visit";

      case CONSULTATION_TYPE_ABBREVIATION_INSOMNIA:
      case CONSULTATION_TYPE_ABBREVIATION_INSOMNIA_FOLLOW_UP:
        return "Start Sleep Visit";
    }

    return "Start Visit";
  }

  renderSignupFormOrComingSoon() {
    const { classes } = this.props;
    if (this.state.checkingLoggedInPatient) {
      return (
        <div className={classes.center} style={{ margin: "24px 0" }}>
          <CircularProgress size={28} />
        </div>
      );
    }
    if (this.state.loggedInPatient) {
      return this.renderContinueAsLoggedInPatient();
    }
    if (!this.state.showComingSoon) {
      return this.renderSignupForm();
    } else {
      return (
        <React.Fragment>
          <h5 className={classes.signupComingSoonTitle}>
            Unfortunately we're not quite ready to prescribe in your area.
            {" We'll let you know as soon as we launch in your state."}
          </h5>
          <div
            style={{
              alignItems: "center",
              display: "flex",
              flexDirection: "column",
              marginTop: "10px",
            }}
          >
            <p style={{ margin: "0 20px 0 20px" }}>
              Here are a few resources that might be helpful in the meantime:
            </p>
            <ul style={{ marginTop: "8px" }}>
              <li>
                <a href="/guides/anxiety/propranolol/">
                  Kick Guide to Calming Your Nerves With Propranolol
                </a>
              </li>
              <li>
                <a href="/guides/anxiety/xanax-vs-propranolol/">
                  Kick Guide to Xanax vs. Beta Blockers
                </a>
              </li>
              <li>
                <a
                  target="_blank"
                  href="https://doctor.webmd.com/"
                  rel="noreferrer"
                >
                  WebMD - Find a doctor near you
                </a>
              </li>
            </ul>
          </div>
          <div className={classes.center}>
            <a href="/">
              <Button
                color="primary"
                style={{ marginTop: "20px", width: "300px" }}
              >
                Back to Homepage
              </Button>
            </a>
          </div>
        </React.Fragment>
      );
    }
  }

  // The step strip and the Delivery / Pickup cards show where both ways of
  // getting the medication exist; generalized-anxiety visits are mail-order
  // only, so gac/gaf keep the plain page. The "coming soon" notice shows
  // without them.
  showsSignupSteps() {
    return (
      isPickupAvailableForConsultationType(this.state.consultationType) &&
      !this.state.showComingSoon
    );
  }

  // The two checkmark lines: under the subhead on master's page, under the
  // Delivery / Pickup cards when those show.
  renderBenefitChecks() {
    const checkStyle = {
      backgroundColor: kickBlue,
      borderRadius: "100%",
      color: "white",
      fontSize: "13px",
      padding: "2px",
      marginRight: "8px",
    };
    return (
      <>
        <p
          style={{
            display: "flex",
            alignItems: "center",
            marginTop: "14px",
          }}
        >
          <Check style={checkStyle} size="small" />
          <span>No appointment, video, or phone call needed</span>
        </p>

        <p
          style={{
            display: "flex",
            alignItems: "center",
            marginTop: "0px",
          }}
        >
          <Check style={checkStyle} size="small" />
          <span>Privately message your doctor anytime</span>
        </p>
      </>
    );
  }

  // The step strip and the Delivery / Pickup cards (design "4F4") above the
  // email field, with the checkmark lines under the cards. The cards only
  // inform. This is the intake-questions-first experiment's
  // "account-first-redesign" arm, shipped to everyone; see
  // docs/intake-questions-first-experiment.md.
  renderSignupSteps() {
    if (!this.showsSignupSteps()) {
      return null;
    }
    // No bottom margin: the last checkmark line's own 10px and the form's top
    // space leave the same 26px below the checkmarks as the 12px gap and the
    // first line's 14px leave above them. The checkmarks then sit 2px lower,
    // because the Email label's line spacing adds about 4px above its letters,
    // so the visible space is the same above and below them.
    return (
      <div
        style={{
          maxWidth: `${MAX_WIDTH}px`,
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <SignupSteps />
        <FulfillmentOptions />
        <div style={{ margin: "2px 0 -2px" }}>{this.renderBenefitChecks()}</div>
      </div>
    );
  }

  render() {
    const { classes } = this.props;
    return (
      <div
        className={classes.container}
        style={{
          backgroundColor: COLOR_KICK_BLUE,
          transition: "opacity 400ms ease-in-out",
        }}
      >
        <GridContainer style={{ maxWidth: "1400px", margin: "auto" }}>
          <GridItem xs={12}>
            <Card className={classes.cardSignup}>
              <GridContainer
                style={{
                  justifyContent: "center",
                }}
              >
                <GridItem
                  xs={12}
                  md={5}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    margin: "20px 0",
                  }}
                  className={classes.gridItemNoPadding}
                >
                  <TrustBox className={classes.signupTrustbox} />
                  <Headline headline={this.renderBenefitHeadline()} />

                  <div
                    style={{
                      maxWidth: `${MAX_WIDTH}px`,
                      width: "100%",
                      alignSelf: "center",
                      backgroundColor: kickLightBlue,
                      padding: "10px 16px",
                      borderRadius: "4px",
                      boxSizing: "border-box",
                    }}
                  >
                    <TestimonialsCarousel
                      consultationType={this.state.consultationType}
                    />
                  </div>
                </GridItem>

                <GridItem xs={12} md={7} style={{}}>
                  <div
                    style={{
                      margin: "10px auto 20px",
                      maxWidth: `${MAX_WIDTH}px`,
                      alignSelf: "center",
                    }}
                  >
                    <h4
                      className={this.props.classes.cardTitle}
                      style={{
                        color: kickBlue,
                        fontSize: "24px",
                        textAlign: "left",
                      }}
                    >
                      {this.renderSubhead()}
                    </h4>

                    {!this.showsSignupSteps() && this.renderBenefitChecks()}
                  </div>

                  {this.renderSignupSteps()}
                  {this.renderSignupFormOrComingSoon()}
                </GridItem>
              </GridContainer>
            </Card>
          </GridItem>
        </GridContainer>
      </div>
    );
  }
}

SignUp.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default withStyles(consultationStyles)(SignUp);
